import React, { useMemo, useState } from "react";
import "./ContactForm.css";
import { Button } from "./Button";

type FormState = {
  name: string;
  email: string;
  topic: "Сотрудничество" | "UGC" | "Контент-пакет" | "Другое";
  message: string;
  consent: boolean;
};

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function ContactForm() {
  const [state, setState] = useState<FormState>({
    name: "",
    email: "",
    topic: "Сотрудничество",
    message: "",
    consent: true
  });

  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!state.name.trim()) e.name = "Введите имя";
    if (!state.email.trim()) e.email = "Введите email";
    else if (!isEmail(state.email)) e.email = "Проверьте формат email";
    if (!state.message.trim()) e.message = "Напишите коротко задачу/вопрос";
    if (!state.consent) e.consent = "Нужно согласие на обработку";
    return e;
  }, [state]);

  const canSubmit = status !== "sending" && Object.keys(errors).length === 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus("sending");
    await new Promise((r) => setTimeout(r, 900)); // демо-отправка
    setStatus("sent");
  };

  return (
    <form className="formCard" onSubmit={onSubmit}>
      <div className="formHead">
        <h3 className="cardTitle">Форма заявки</h3>
        <div className={`formStatus ${status}`}>
          {status === "idle" && "ответим в течение 24–48 часов"}
          {status === "sending" && "отправляем…"}
          {status === "sent" && "сообщение отправлено (демо)"}
        </div>
      </div>

      <div className="formGrid">
        <label className="field">
          <span className="label">Имя</span>
          <input
            className={`input ${errors.name ? "invalid" : ""}`}
            value={state.name}
            onChange={(ev) => setState((s) => ({ ...s, name: ev.target.value }))}
            placeholder="Кристина / Анна / Бренд"
            autoComplete="name"
          />
          {errors.name ? <span className="error">{errors.name}</span> : null}
        </label>

        <label className="field">
          <span className="label">Email</span>
          <input
            className={`input ${errors.email ? "invalid" : ""}`}
            value={state.email}
            onChange={(ev) => setState((s) => ({ ...s, email: ev.target.value }))}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
          />
          {errors.email ? <span className="error">{errors.email}</span> : null}
        </label>

        <label className="field full">
          <span className="label">Тема</span>
          <select
            className="input select"
            value={state.topic}
            onChange={(ev) => setState((s) => ({ ...s, topic: ev.target.value as FormState["topic"] }))}
          >
            <option>Сотрудничество</option>
            <option>UGC</option>
            <option>Контент-пакет</option>
            <option>Другое</option>
          </select>
        </label>

        <label className="field full">
          <span className="label">Сообщение</span>
          <textarea
            className={`input textarea ${errors.message ? "invalid" : ""}`}
            value={state.message}
            onChange={(ev) => setState((s) => ({ ...s, message: ev.target.value }))}
            placeholder="Коротко: что за продукт/задача, формат, дедлайн, ссылка на сайт/соцсети (если есть)."
            rows={6}
          />
          {errors.message ? <span className="error">{errors.message}</span> : null}
        </label>
      </div>

      <label className={`consentRow ${errors.consent ? "invalidRow" : ""}`}>
        <input
          type="checkbox"
          checked={state.consent}
          onChange={(ev) => setState((s) => ({ ...s, consent: ev.target.checked }))}
        />
        <span>
          Я согласен(на) на обработку данных для связи по заявке.{" "}
          <span className="consentHint">(демо)</span>
        </span>
      </label>
      {errors.consent ? <div className="error">{errors.consent}</div> : null}

      <div className="formActions">
        <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
          Отправить
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={() => {
            setStatus("idle");
            setState({ name: "", email: "", topic: "Сотрудничество", message: "", consent: true });
          }}
          disabled={status === "sending"}
        >
          Очистить
        </Button>
      </div>

      <div className="formFoot">
        Подсказка: если вам нужно NDA/договор/медиакит — напишите об этом, и я пришлю варианты.
      </div>
    </form>
  );
}